import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useLayoutEffect, useRef } from 'react';

import { patchList } from './patch-list';
import { useRealtimeEvent } from './useRealtimeEvent';

/**
 * Apply a pushed record to a cached list without going back to the server.
 *
 * The counterpart to useRefetchOnRealtime, which stays the right tool wherever the signal
 * carries no data. Use this one only where the event's room is already exactly the audience
 * allowed to read what it carries. `accepts` narrows that further for a list that shows only part
 * of what its room receives.
 */
export function useRealtimePatch<T>(
  event: string,
  queryKey: readonly unknown[],
  idOf: (row: T) => string,
  accepts?: (row: T) => boolean
): void {
  const queryClient = useQueryClient();
  // Call sites pass an inline array, so its identity changes every render; hold the latest in
  // a ref (as useRefetchOnRealtime does) to keep the subscription stable.
  const keyRef = useRef(queryKey);
  useLayoutEffect(() => {
    keyRef.current = queryKey;
  });

  const onEvent = useCallback(
    (item: T) => {
      if (accepts && !accepts(item)) return;
      queryClient.setQueryData<T[]>(keyRef.current, current => patchList(current, item, idOf));
    },
    [queryClient, idOf, accepts]
  );

  useRealtimeEvent<T>(event, onEvent);
}
